/**
 * Backend API Gateway. Use for any requests you make to the backend API.
 */

import { msalInstance } from "../main";
import { loginRequest } from "./authConfig";
import { BACKEND_BASE } from "./config";

/**
 * Make a request to the backend API which expects a JSON response
 */
export const backendRequestJson = async (
  method,
  url,
  body = null,
  headers = null,
  allowLoginRedirect = true
) => {
  // Construct fetch options
  const options = {
    method,
    headers: { "Content-Type": "application/json", ...(headers || {}) },
    credentials: "include", // include session cookies in the request
  };
  if (body) options.body = JSON.stringify(body);

  let response = await fetch(`${BACKEND_BASE}${url}`, options);

  // If request is successful, return the response JSON
  if (response.ok) return await response.json();

  // If session expired, try to recreate it
  if (response.status === 401 || response.status === 403) {
    console.log(
      `Backend replied HTTP ${response.status}. Trying to recreate session...`
    );

    const ok = await createSession();
    if (!ok) {
      if (allowLoginRedirect) {
        alert("Session expired, please log in again. Redirecting to login page.");
        await msalInstance.loginRedirect(loginRequest);
      } else
        console.log(
          "Backend request failed due to session expiry. Not redirecting to login page."
        );

      // TODO: uncomment this
      //
    }
    console.log("> OK (re)creating session");

    // Retry original request after session creation
    response = await fetch(`${BACKEND_BASE}${url}`, options);

    if (response.ok) return await response.json();
  }

  throw new Error(`Request failed: ${response.status} ${response.statusText}`);
};

/** Create a session in the backend */
export const createSession = async () => {
  try {
    const idToken = await getIdToken();

    // Send ID token to backend to create a session
    const response = await fetch(`${BACKEND_BASE}/auth/sessions/create`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${idToken}`,
      },
      credentials: "include", // Ensure cookies are sent with requests
      body: JSON.stringify({ foo: "bar" }),
    });

    if (!response.ok) {
      throw new Error(`Session creation failed: ${response.statusText}`);
    }

    return true;
  } catch (error) {
    console.log("Failed to create session:", error);
    return false;
  }
};

/** Obtain the ID Token from MSAL */
export const getIdToken = async () => {
  try {
    const account = msalInstance.getActiveAccount();
    if (!account) {
      throw new Error("No active account found. User must log in.");
    }

    // Request an ID token silently
    const response = await msalInstance.acquireTokenSilent({
      ...loginRequest,
      account: account,
    });

    return response.idToken;
  } catch (error) {
    // console.log("Failed to get ID token:", error);
    throw new Error("Authentication required" + String(error));
  }
};
